import { IconClose } from '@consta/icons/IconClose'
import { Button } from '@consta/uikit/Button'
import { Combobox } from '@consta/uikit/Combobox'
import { DatePicker } from '@consta/uikit/DatePicker'
import { Layout } from '@consta/uikit/Layout'
import { Modal } from '@consta/uikit/Modal'
import { Text } from '@consta/uikit/Text'
import { TextField } from '@consta/uikit/TextField'
import type { ProjectType } from '@core/core/types'
import { getProjectDataAnalyticParams } from '@core/core/utils'
import type { FC } from 'react'
import { useMemo } from 'react'

import css from './CreateProjectModal.css'
import { useProjectDictionaries, useProjectInfoApply, useProjectInfoEditor } from './hooks'
import type { ComboItem, CreateComboBoxItemType, CreateProjectModalProps } from './types'

const currentYear = new Date().getFullYear().toString()

function createComboItems<T extends string>(arr?: CreateComboBoxItemType<T>): ComboItem<T>[] {
  return (
    arr?.map(obj => ({
      label: obj.name,
      id: obj.id,
      disabled: false,
    })) ?? []
  )
}

export const CreateProjectModal: FC<CreateProjectModalProps> = ({
  isModalOpen,
  closeModal,
  projectToUpdate,
}) => {
  const {
    project,
    handleProjectTypeChange,
    handleYearChange,
    handleNameChange,
    handleFieldChange,
    handleOrgChange,
  } = useProjectInfoEditor(projectToUpdate)
  const { companies, fields, projectTypes } = useProjectDictionaries(project.companyId ?? undefined)
  const handleApply = useProjectInfoApply(project, closeModal)

  const organizationsItems = useMemo(() => createComboItems(companies), [companies])
  const projectTypesItems = useMemo(
    () => createComboItems<ProjectType>(projectTypes),
    [projectTypes],
  )
  const fieldsItems = useMemo(() => createComboItems(fields), [fields])

  const canCreate = !!(project.type && project.companyId && project.name.trim())
  const isUpdateModal = projectToUpdate !== undefined

  const projectDate = project.year ? new Date(project.year, 0, 1) : null
  const dataAnalyticsId = isUpdateModal ? 'project_edit_update' : 'project_new_create'

  return (
    <Modal
      className={css.dialog}
      isOpen={isModalOpen}
      hasOverlay={true}
      onClickOutside={closeModal}
      onEsc={closeModal}>
      <div className={css.dlgHeader}>
        <Text as="p" size="2xl" view="primary" weight="bold">
          {isUpdateModal ? 'Редактирование проекта' : 'Создание проекта'}
        </Text>
        <Button size="s" view="clear" iconRight={IconClose} onlyIcon={true} onClick={closeModal} />
      </div>

      <Layout direction="column" className={css.dlgLayout}>
        <div className={css.dlgRow}>
          <TextField
            type="text"
            size="s"
            labelPosition="top"
            label="Название"
            value={project.name}
            required={true}
            onChange={handleNameChange}
          />
        </div>

        <div className={css.dlgLongRow}>
          <DatePicker
            type="year"
            className={css.dlgLongRowItem}
            size="s"
            label="Год"
            placeholder={currentYear}
            value={projectDate}
            onChange={handleYearChange}
          />
          <div className={css.dlgLongRowItem}>
            <Combobox
              className={css.removeClearIndicator}
              size="s"
              label="Тип"
              labelPosition="top"
              items={projectTypesItems}
              value={projectTypesItems.find(item => item.id === project.type)}
              required={true}
              onChange={handleProjectTypeChange}
            />
          </div>
        </div>
        <div className={css.dlgRow}>
          <Combobox
            className={css.removeClearIndicator}
            size="s"
            labelPosition="top"
            label="Дочернее общество"
            items={organizationsItems}
            value={organizationsItems.find(item => item.id === project.companyId)}
            required={true}
            onChange={handleOrgChange}
          />
        </div>
        <div className={css.dlgRow}>
          <Combobox
            size="s"
            labelPosition="top"
            label="Месторождение"
            items={fieldsItems}
            value={fieldsItems.find(item => item.id === project.fieldId)}
            onChange={handleFieldChange}
          />
        </div>
      </Layout>

      <div className={css.dlgControls}>
        <Button size="m" view="ghost" label="Отмена" onClick={closeModal} />
        <Button
          size="m"
          view="primary"
          label={isUpdateModal ? 'Обновить' : 'Создать'}
          disabled={!canCreate}
          data-analytics-id={dataAnalyticsId}
          data-analytic-params={getProjectDataAnalyticParams(project.name, project.type)}
          onClick={handleApply}
        />
      </div>
    </Modal>
  )
}
